import { isMobile } from "../../utils"

/**
 * @typedef {Object} ColumnOptions
 * @property {string} id
 * @property {string} label
 * @property {number|string} width
 * @property {number} minWidth
 * @property {string} type
 * @property {"left"|"center"|"right"} align
 * @property {"top"|"center"|"bottom"} vAlign
 * @property {Column[]} children
 * @property {boolean} fixed
 * @property {boolean} readonly
 * @property {boolean} sortable
 * @property {string} sortTargetColId
 * @property {boolean} filterable
 * @property {boolean} hidden
 * @property {(rowData:any)=>boolean} unmodifiableRule
 */

export default class Column {
	static get DEFAULT_PROPS () {
		return {
			width: 200,
			align: "center",
			vAlign: "center",
			cursor: "default",
			unmodifiableRule: () => false
		}
	}

	static ARROW_KEYS = [
		"ArrowRight",
		"ArrowLeft",
		"ArrowUp",
		"ArrowDown"
	]

	static INPUT_END_KEYS = [
		"Enter",
		"Tab",
		"Escape"
	]

	/**
	 * @type {ColumnOptions}
	 */
	options

	/**
	 * @type {() => any}
	 */
	textboxtCustomEvents

	/**
	 * 他のカラムも考慮した幅
	 */
	#actualWidth

	set actualWidth (val) {
		this.#actualWidth = val
	}

	get actualWidth () {
		return this.#actualWidth
	}

	get id () {
		return this.options.id
	}

	get readonly () {
		return this.options.readonly
	}

	get cursor () {
		return "default"
	}

	get rowSpan () {
		return this.options.rowSpan || 1
	}

	get type () {
		return this.options.type
	}

	get fixed () {
		return this.options.fixed
	}

	get sortable () {
		return this.options.sortable
	}

	get sortTargetCol () {
		return this.options.sortTargetColId
	}

	get filterable () {
		return this.options.filterable
	}

	static SORT_DIRS = {
		ASC: 0,
		DESC: 1,
		NONE: 2
	}

	sortDir = Column.SORT_DIRS.NONE

	/**
	 * @type {string[]}
	 */
	filteredValues = []

	get isFiltered () {
		return this.filteredValues.length > 0
	}

	get hidden () {
		return this.options.hidden
	}

	get sortIconSize () {
		return 8
	}

	get filterIconSize () {
		return 10
	}

	#inputIsDeleted = true

	#cachedLabelTextLinesInfo = null

	constructor ({ options, textboxtCustomEvents = () => ({}) }) {
		this.options = {
			...Column.DEFAULT_PROPS,
			...options
		}
		this.textboxtCustomEvents = textboxtCustomEvents
	}

	updateOptions (options) {
		this.options = {
			...this.options,
			...options
		}
	}

	renderHeaderCell (ctx, { offsetLeft, offsetTop = 0, height }) {
		ctx.useContext2d(ctx2d => {
			const text = this.options.label
			// セル描画
			ctx2d.strokeStyle = ctx.gridStyle.borderColor
			ctx2d.lineWidth = ctx.gridStyle.borderLineWidth
			ctx2d.fillStyle = ctx.gridStyle.headerBgColor
			ctx.fillRect(offsetLeft, offsetTop, this.actualWidth, height)
			ctx.strokeRect(offsetLeft, offsetTop, this.actualWidth, height)

			let remainingWidth = this.actualWidth

			// アイコン描画
			if (this.sortable) {
				const arrowSize = this.sortIconSize
				const width = remainingWidth
				this.renderArrow(ctx, { offsetLeft, offsetTop, height, width, arrowSize })
				remainingWidth -= arrowSize + ctx.cellHPadding * 2
			}

			if (this.filterable) {
				const iconSize = this.filterIconSize
				const width = remainingWidth
				this.renderFilterIcon(ctx, { offsetLeft, offsetTop, height, width, iconSize })
				// const paddingTop = (height - iconSize) / 2
				// const img = this.isFiltered ? filterAppliedImg : filterImg
				// ctx.drawImage(img, offsetLeft + remainingWidth - iconSize - ctx.cellHPadding, offsetTop + paddingTop, iconSize, iconSize)
				remainingWidth -= iconSize + ctx.cellHPadding * 2
			}

			// this.renderMultilineCell(ctx, { offsetTop, offsetLeft, height, value: text, color: ctx.gridStyle.headerTxtColor })
			// セルの中の描画
			this.renderMultilineText(ctx, {
				offsetTop,
				offsetLeft,
				height,
				width: remainingWidth,
				value: text,
				color: ctx.gridStyle.headerTxtColor
			})
		})
	}

	renderFilterIcon (ctx, { offsetLeft, offsetTop, height, width, iconSize }) {
		ctx.useContext2d(ctx2d => {
			const arrowTotalWidth = iconSize + ctx.cellHPadding * 2
			const iconTop = offsetTop + height / 2 - iconSize / 2
			const iconBottom = offsetTop + height / 2 + iconSize / 2
			const iconLeft = offsetLeft + width - arrowTotalWidth + ctx.cellHPadding
			const midWidth = iconSize * 2 / 3
			const midLeft = iconLeft + (iconSize - midWidth) / 2
			const botWidth = iconSize * 1 / 3
			const botLeft = iconLeft + (iconSize - botWidth) / 2

			ctx2d.strokeStyle = this.isFiltered ? ctx.gridStyle.headerTxtColor : "#cccccc"
			ctx2d.lineWidth = 2
			ctx.strokeLine(iconLeft, iconTop, iconLeft + iconSize, iconTop)
			ctx.strokeLine(midLeft, iconTop + iconSize / 2, midLeft + midWidth, iconTop + iconSize / 2)
			ctx.strokeLine(botLeft, iconBottom, botLeft + botWidth, iconBottom)
		})
	}

	/**
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {*} param1
	 */
	renderArrow (ctx, { offsetLeft, offsetTop, height, width, arrowSize }) {
		ctx.useContext2d(ctx2d => {
			const dev2pixRatio = (num) => num * window.devicePixelRatio
			const arrowDisableColor = "#cccccc"
			const arrowTotalWidth = arrowSize + ctx.cellHPadding * 2
			const arrowTop = offsetTop + height / 2 - arrowSize
			const arrowBottom = offsetTop + height / 2 + arrowSize
			const arrowLeft = offsetLeft + width - arrowTotalWidth + ctx.cellHPadding

			const renderTopArrow = () => {
				ctx2d.beginPath()
				ctx2d.moveTo(dev2pixRatio(arrowLeft + arrowSize / 2), dev2pixRatio(arrowTop))
				ctx2d.lineTo(dev2pixRatio(arrowLeft), dev2pixRatio(offsetTop + height / 2 - 2))
				ctx2d.lineTo(dev2pixRatio(arrowLeft + arrowSize), dev2pixRatio(offsetTop + height / 2 - 2))
				ctx2d.fill()
			}
			const renderBottomArrow = () => {
				ctx2d.beginPath()
				ctx2d.moveTo(dev2pixRatio(arrowLeft + arrowSize / 2), dev2pixRatio(arrowBottom))
				ctx2d.lineTo(dev2pixRatio(arrowLeft), dev2pixRatio(offsetTop + height / 2 + 2))
				ctx2d.lineTo(dev2pixRatio(arrowLeft + arrowSize), dev2pixRatio(offsetTop + height / 2 + 2))
				ctx2d.fill()
			}

			if (this.sortDir === Column.SORT_DIRS.NONE) {
				ctx2d.fillStyle = arrowDisableColor
				renderTopArrow()
				renderBottomArrow()
			} else if (this.sortDir === Column.SORT_DIRS.DESC) {
				ctx2d.fillStyle = arrowDisableColor
				renderTopArrow()
				ctx2d.fillStyle = ctx.gridStyle.headerTxtColor
				renderBottomArrow()
			} else if (this.sortDir === Column.SORT_DIRS.ASC) {
				ctx2d.fillStyle = ctx.gridStyle.headerTxtColor
				renderTopArrow()
				ctx2d.fillStyle = arrowDisableColor
				renderBottomArrow()
			}
		})
	}

	renderCell (ctx, renderProps) {
		this.renderCellDefault(ctx, renderProps)
	}

	/**
	 *
	 * @param {import("./common").Context} ctx
	 * @param {*} param1
	 */
	renderCellDefault (ctx, {
		offsetTop,
		offsetLeft,
		height,
		value,
		color,
		rowIndex,
		isCurrentCell,
		cellInfo,
		isUnmodifiable
	}) {
		ctx.useContext2d(ctx2d => {
			const {
				borderColor,
				borderLineWidth,
				bgColor,
				txtColor,
				selectBgColor
			} = ctx.gridStyle
			// セル描画
			ctx2d.strokeStyle = borderColor
			ctx2d.lineWidth = borderLineWidth
			ctx2d.fillStyle = bgColor
			if (cellInfo) {
				ctx2d.fillStyle = cellInfo.bgColor || ctx2d.fillStyle
			}
			if (isCurrentCell) {
				ctx2d.fillStyle = selectBgColor
			}
			ctx.fillRect(offsetLeft, offsetTop, this.actualWidth, height)
			ctx.strokeRect(offsetLeft, offsetTop, this.actualWidth, height)

			if (isUnmodifiable) {
				ctx.strokeLine(
					offsetLeft,
					height + offsetTop,
					this.actualWidth + offsetLeft,
					offsetTop
				)
				return // セルの値は描画しない
			}

			// セルの中の描画
			ctx2d.fillStyle = color || txtColor
			if (cellInfo) {
				ctx2d.fillStyle = cellInfo.color || ctx2d.fillStyle
			}
			const text = value != null ? String(value) : "-"

			this.renderText(ctx, {
				text,
				offsetLeft,
				offsetTop,
				height
			})
		})
	}

	renderText (ctx, {
		text,
		offsetLeft,
		offsetTop,
		height,
		width = this.actualWidth,
		align = this.options.align,
		vAlign = this.options.vAlign
	}) {
		const { left, top } = this.getTextPosition(ctx, {
			text,
			left: offsetLeft,
			top: offsetTop,
			height,
			width,
			align,
			vAlign
		})
		ctx.fillText(text, left, top, width)
	}

	// 複数行の描画(ヘッダ用)
	renderMultilineText (ctx, {
		offsetTop,
		offsetLeft,
		height,
		width,
		value,
		color
	}) {
		ctx.useContext2d(ctx2d => {
			ctx2d.fillStyle = color || ctx.gridStyle.txtColor
			const text = value != null ? String(value) : ""

			// グリッドのラベルを再設定する
			const textList = [...this.splitTextByWidth(ctx2d, width, text)]

			// テキストの行間(0だと重なるので大きめに)
			const linePadding = 4
			// 行高さ
			const textLineHeight = ctx.fontSize
			// テキスト全行の高さ
			let textListHeight = (textLineHeight + linePadding) * textList.length

			// 縦にはみ出すテキストは描画しない
			if (height < textListHeight) {
				const overflowedHeight = textListHeight - height

				const removeLineSize = Math.ceil(overflowedHeight / (textLineHeight + linePadding))

				textListHeight -= (textLineHeight) * removeLineSize
				textList.splice(-removeLineSize)
			}

			let textListTop = offsetTop + height / 2 - textListHeight / 2
			if (textListTop < offsetTop) textListTop = offsetTop

			textList.forEach((text, index) => {
				const { left, top } = this.getTextPosition(ctx, {
					text,
					left: offsetLeft,
					top: textListTop,
					height: textLineHeight,
					width: width,
					align: "center",
					vAlign: "center"
				})
				ctx.fillText(text, left, top, width)
				textListTop += textLineHeight + linePadding
			})
		})
	}

	// 複数行の描画(セル用)
	renderMultilineTextForCell (ctx, {
		offsetTop,
		offsetLeft,
		height,
		width,
		value,
		color,
		align = this.options.align
	}) {
		ctx.useContext2d(ctx2d => {
			ctx2d.fillStyle = color || ctx.gridStyle.txtColor
			const text = value != null ? String(value) : ""

			const textList = text.split("\n")

			// テキストの行間(0だと重なるので大きめに)
			const linePadding = 4
			// 行高さ
			const textLineHeight = ctx.fontSize
			// テキスト全行の高さ
			let textListHeight = (textLineHeight + linePadding) * textList.length

			// 縦にはみ出すテキストは描画しない
			if (height < textListHeight) {
				const overflowedHeight = textListHeight - height

				const removeLineSize = Math.ceil(overflowedHeight / (textLineHeight + linePadding))

				textListHeight -= (textLineHeight) * removeLineSize
				textList.splice(-removeLineSize)
			}

			let textListTop = offsetTop + height / 2 - textListHeight / 2
			if (textListTop < offsetTop) textListTop = offsetTop

			textList.forEach((text, index) => {
				const { left, top } = this.getTextPosition(ctx, {
					text,
					left: offsetLeft,
					top: textListTop,
					height: textLineHeight,
					width: width,
					align: align,
					vAlign: "center"
				})
				ctx.fillText(text, left, top, width)
				textListTop += textLineHeight + linePadding
			})
		})
	}

	/**
	 *
	 * @param {CanvasRenderingContext2D} ctx2d
	 * @param {number} widthExcludePadding パディング抜きの幅（つまり、これ以上テキストがはみ出せない幅）
	 * @param {string} text
	 */
	splitTextByWidth (ctx2d, widthIncludePadding, text) {
		const allChars = text.split("")

		const ellipsis = "…"
		const ellipsisWidth = ctx2d.measureText(ellipsis).width

		const lines = []
		let currentLine = ""
		allChars.forEach(char => {
			if (char === "\n") {
				lines.push(currentLine)
				currentLine = ""
				return
			}
			currentLine += char
			if (ctx2d.measureText(currentLine).width > (widthIncludePadding * window.devicePixelRatio - ellipsisWidth)) {
				lines.push(currentLine)
				currentLine = ""
			}
		})
		if (currentLine) lines.push(currentLine)

		this.#cachedLabelTextLinesInfo = {
			width: this.actualWidth,
			lines
		}

		return lines
	}

	flush (ctx) {
		// do nothing
	}

	editCell (ctx, onEdit, notifyCellEditEnd) {
		this.#inputIsDeleted = false
		if (this.readonly) return
		this.onCellEdit(ctx, onEdit, notifyCellEditEnd)
	}

	onCellSelected (ctx, setValue) {
	}

	/**
	 *
	 * @param {import("./common").Context} ctx
	 * @param {*} cell
	 */
	onCellEdit (ctx, setValue, notifyCellEditEnd) {
		const textbox = document.createElement("input")
		textbox.type = "text"
		textbox.setAttribute("spellcheck", false)
		textbox.classList.add("text")
		textbox.classList.add("hide")
		textbox.style.font = `${ctx.fontSize}pt ${ctx.font}`
		if (this.options.unmodifiableRule(ctx.currentCellInfo.row.data)) {
			textbox.setAttribute("readonly", true)
		}

		const uncover = () => {
			textbox.value = ctx.currentCellInfo.value != null ? ctx.currentCellInfo.value : ""
			textbox.classList.remove("hide")
			Object.entries(this.textboxtCustomEvents(setValue)).forEach(([key, value]) => {
				textbox.addEventListener(key, value)
			})
			// テキスト編集中はグリッドのコピペ処理が動かないようにする
			textbox.addEventListener("copy", e => e.stopPropagation())
			textbox.addEventListener("paste", e => e.stopPropagation())
		}

		textbox.addEventListener("blur", () => {
			notifyCellEditEnd()
			// ここでフォーカスすると、２つ以上グリッドがあった場合にフォーカスの奪い合いになり動作しなくなる
			this.onCellEditEnd(ctx, false)
		}, { once: true })

		textbox.addEventListener("input", e => {
			setValue(e.target.value)
			if (!ctx.isPasting) {
				uncover()
			}
		})

		textbox.addEventListener("click", () => {
			if (this.options.unmodifiableRule(ctx.currentCellInfo.row.data)) return
			uncover()
		}, { once: true })

		textbox.addEventListener("keydown", e => {
			if (this.options.unmodifiableRule(ctx.currentCellInfo.row.data)) return
			if (Column.INPUT_END_KEYS.includes(e.key)) {
				if (textbox.classList.contains("hide") && e.key === "Enter") {
					uncover()
					return
				}
				this.onCellEditEnd(ctx, true)
				this.onCellEdit(ctx, setValue, notifyCellEditEnd)
			} else if (textbox.classList.contains("hide") && e.key === "F2") {
				uncover()
			} else if (!Column.ARROW_KEYS.includes(e.key) && !e.ctrlKey && !e.key === "Delete") {
				if (textbox.classList.contains("hide")) {
					uncover()
				}
			}
		})

		const inputWrapper = ctx.inputEl
		inputWrapper.appendChild(textbox)
		setTimeout(() => {
			textbox.focus()
		})
	}

	/**
	 *
	 * @param {*} ctx
	 * @param {*} requestFocus
	 * @returns
	 */
	onCellEditEnd (ctx, requestFocus = true) {
		if (this.#inputIsDeleted) return
		this.#inputIsDeleted = true
		const inputEl = ctx.inputEl
		while (inputEl.firstChild) {
			inputEl.removeChild(inputEl.firstChild)
		}
		if (requestFocus && !isMobile) ctx.controlEl.focus()
	}

	getTextPosition (ctx, { left, top, height, width, align, vAlign }) {
		const ctx2d = ctx.ctx2d

		switch (align) {
		case "left":
			ctx2d.textAlign = "left"
			left += ctx.cellHPadding
			break
		case "center":
			ctx2d.textAlign = "center"
			left += width / 2
			break
		case "right":
			ctx2d.textAlign = "right"
			left += width - ctx.cellHPadding
			break
		}

		switch (vAlign) {
		case "top":
			ctx2d.textBaseline = "top"
			top += ctx.cellVPadding
			break
		case "center":
			ctx2d.textBaseline = "middle"
			top += height / 2
			break
		case "bottom":
			ctx2d.textBaseline = "bottom"
			top += height - ctx.cellVPadding
			break
		}

		return {
			left,
			top
		}
	}

	sort = (prev, next) => {
		const isAsc = this.sortDir === Column.SORT_DIRS.ASC

		const prevValue = prev.data[this.id]
		const nextValue = next.data[this.id]

		if (prevValue == null || nextValue == null) {
			if (prevValue != null) {
				return isAsc ? 1 : -1
			}
			if (nextValue != null) {
				return isAsc ? -1 : 1
			}
			return 0
		}

		if (prevValue < nextValue) {
			return isAsc ? -1 : 1
		}
		if (prevValue > nextValue) {
			return isAsc ? 1 : -1
		}
		return 0
	}
}
